import { type ReactNode, createContext, useContext } from "react"

/**
 * OKS-400 — Preview-mode context.
 *
 * Drives the read-only treatment of the creator profile when a brand
 * admin views it via `/crew/:id/profile_preview`. When the value is
 * `true`, the profile's interactive affordances (Rewards tile, Connect
 * TikTok, "See all" activity, "View all" badges) render INERT — a muted,
 * `aria-disabled` element that issues no navigation and no request
 * (UX Spec §2 "Inert interactions").
 *
 * Default is `false`: components that read this hook outside a provider
 * (i.e. the creator's own `/profile`) behave exactly as before — zero
 * behavioural change off the preview path.
 */
const PreviewModeContext = createContext(false)

export function PreviewModeProvider({
  value,
  children,
}: {
  value: boolean
  children: ReactNode
}) {
  return (
    <PreviewModeContext.Provider value={value}>
      {children}
    </PreviewModeContext.Provider>
  )
}

/** @returns true when rendering inside a read-only profile preview. */
export function usePreviewMode(): boolean {
  return useContext(PreviewModeContext)
}
